import { Alert, Button, Modal, message } from "antd";
import type { DeliveryVo } from "@zhihaoscm/entity";
import { DELIVERY_DETAIL_TAB_PC, DELIVERY_STATE } from "@zhihaoscm/meta";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { deliveryConfirm, getDeliveryVo } from "@/api/deliveryService";
import { Breadcrumb, DetailHeader, ZhInsetTabs } from "@/components/common";
import { DeliveryInfo } from "./components/DeliveryInfo";
import { DeliveryLogisticsTracking } from "./components/DeliveryLogisticsTracking";
import { DeliveryPayHistory } from "./components/DeliveryPayHistory";
import { DeliveryProofMaterials } from "./components/DeliveryProofMaterials";
import "./delivery-detail.css";

/** 旧版待确认提货倒计时为发起时间后的 48 小时。 */
function countdown(value?: string) {
    const rest = value ? dayjs(value).add(48, "hour").valueOf() - Date.now() : 0;
    if (rest <= 0) return "";
    const hours = Math.floor(rest / 3600000);
    const minutes = Math.floor((rest % 3600000) / 60000);
    const seconds = Math.floor((rest % 60000) / 1000);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")} 后自动确认提货`;
}

/** 提货详情沿用旧版连续区块，顶部 Tab 仅滚动定位。 */
export function DeliveryDetailPage() {
    const { id = "" } = useParams();
    const navigate = useNavigate();
    const [detail, setDetail] = useState<DeliveryVo>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [active, setActive] = useState(DELIVERY_DETAIL_TAB_PC.DELIVERY_MESSAGE.code);
    const [, setClock] = useState(0);
    const load = async () => {
        if (!id) {
            setError(true);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(false);
        try {
            setDetail(await getDeliveryVo(id));
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    useEffect(() => {
        const timer = window.setInterval(() => setClock(value => value + 1), 1000);
        return () => window.clearInterval(timer);
    }, []);
    useEffect(() => {
        const initiationTime = detail?.delivery?.initiationTime;
        if (Number(detail?.delivery?.state) !== DELIVERY_STATE.TO_BE_CONFIRMED.code || !initiationTime) return;
        const remaining = dayjs(initiationTime).add(48, "hour").valueOf() - Date.now();
        if (remaining <= 0) {
            void load();
            return;
        }
        // Vue initiationTime 到期会触发 refresh；这里按相同截止时刻重新拉取详情。
        const timer = window.setTimeout(() => void load(), remaining);
        return () => window.clearTimeout(timer);
    }, [detail?.delivery?.initiationTime, detail?.delivery?.state]);
    const confirm = () =>
        Modal.confirm({
            title: "确认提货",
            content: "是否确认提货",
            onOk: async () => {
                await deliveryConfirm([id]);
                void message.success("操作成功");
                await load();
            }
        });
    const delivery = detail?.delivery;
    const deliveryState = Number(delivery?.state);
    const tabs = useMemo(() => {
        if (!delivery) return [];
        // 与 Vue 一样直接使用元数据选项，仅按提货状态过滤付款记录。
        return DELIVERY_DETAIL_TAB_PC.options.filter(option =>
            Number(option.value) !== DELIVERY_DETAIL_TAB_PC.COLLECTION_HISTORY.code ||
            [DELIVERY_STATE.CONFIRMED.code, DELIVERY_STATE.PICKED_UP.code].includes(deliveryState)
        ).map(option => ({ label: option.label, value: Number(option.value) }));
    }, [delivery, deliveryState]);
    // 旧 Vue 详情首屏静默等待数据，不额外展示页面级加载框。
    if (loading) return null;
    if (error || !detail || !delivery)
        return (
            <Alert
                type="error"
                showIcon
                message={id ? "提货详情加载失败" : "缺少提货编号"}
                action={id ? <Button onClick={() => void load()}>重试</Button> : undefined}
            />
        );
    const goSection = (value: number) => {
        const sectionIds: Record<number, string> = {
            [DELIVERY_DETAIL_TAB_PC.DELIVERY_MESSAGE.code]: "delivery-info",
            [DELIVERY_DETAIL_TAB_PC.COLLECTION_HISTORY.code]: "delivery-payment",
            [DELIVERY_DETAIL_TAB_PC.CORROBORATION.code]: "delivery-proof",
            [DELIVERY_DETAIL_TAB_PC.LOGISTICS_TRUCK.code]: "delivery-logistics"
        };
        setActive(value);
        document.getElementById(sectionIds[value])?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    return (
        <section className="delivery-detail-page">
            <div className="section-card delivery-detail-top">
                <Breadcrumb routes={[{ name: "交易中心", path: "/orderCenter/myOrder" }, { name: "我的订单", path: `/orderCenter/myOrder/detail/${delivery.orderId}` }, { name: "提货详情" }]} />
                <div className="delivery-detail-header">
                    <DetailHeader
                        title="提货详情"
                        leftInfos={[{ label: "编号", value: delivery.id }]}
                        extra={
                            <div className="delivery-detail-state">
                                {deliveryState === DELIVERY_STATE.TO_BE_CONFIRMED.code ? (
                                    <small>{countdown(delivery.initiationTime)}</small>
                                ) : null}
                                <strong>{DELIVERY_STATE.getIntroByCode(deliveryState) ?? "--"}</strong>
                            </div>
                        }
                    />
                    <span>
                        创建时间：
                        {delivery.createdTime ? dayjs(delivery.createdTime).format("YYYY-MM-DD HH:mm") : "--"}
                    </span>
                </div>
                <ZhInsetTabs active={active} onChange={goSection} options={tabs} />
            </div>
            <div className="delivery-detail-content">
                <DeliveryInfo data={detail} />
                {tabs.some(tab => tab.value === DELIVERY_DETAIL_TAB_PC.COLLECTION_HISTORY.code) ? <DeliveryPayHistory data={detail} /> : null}
                <DeliveryProofMaterials data={detail} />
                <DeliveryLogisticsTracking data={detail} />
            </div>
            <div className="delivery-detail-actions">
                <Button type="primary" onClick={() => navigate(`/orderCenter/myOrder/detail/${delivery.orderId}`)}>
                    返回
                </Button>
                {deliveryState === DELIVERY_STATE.TO_BE_CONFIRMED.code ? (
                    <Button type="primary" onClick={confirm}>
                        确认提货
                    </Button>
                ) : null}
            </div>
        </section>
    );
}
